import React, { useState } from "react";
import hideIcon from "../../../img/icons/hide-eye.svg";
import modalHideIcon from "../../../img/icons/Hide-column.svg";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { hideParameter } from "../../../actions/filters";
import ColumnSort from "./ColumnSort";
import "./style.css";
import DragComponent from "../../DragComponent";
import dragAndDropIcon from "../../../img/icons/drag-and-drop.svg";
import localization from "../../../data/config";
import { setModalState, openModal, closeModal } from "../../../actions/modal";
import { removeFilter } from "../../../utilities/removeFilter";
import ToolTip from "../../ToolTip";

/**
 * @name Columns
 * @function
 * @description Displays Table Header Columns
 * @returns {JSX}
 * @param {*} props
 */
const Columns = (props) => {
  const [dontAskAgain, setDontAskAgain] = useState(
    localStorage.getItem("dontAskAgainPreference") === "true"
  );
  const { newSort, sortPriority, dragAndDropLabel, hideColumnLabel } = useSelector((state) => ({
    newSort: state.filters.newSort,
    sortPriority: state.filters.sortPriority,
    dragAndDropLabel: state.labels.button.dragAndDrop,
    hideColumnLabel: state.labels.button.hideColumn
  }));
  const dispatch = useDispatch();

  /**
   * @name renderColumn
   * @function
   * @const
   * @description Function to render columns
   */
  const renderColumn = (header) => {
    /**
     * @name handleHideIconClick
     * @function
     * @const
     * @description Function to hide column when hideicon is clicked.
     */
    const handleHideIconClick = () => {
      if (dontAskAgain) {
        dispatch(hideParameter(header.id));
        removeFilter(header.id);
      } else {
        dispatch(
          setModalState({
            modalIcon: modalHideIcon,
            modalTitle: localization.confirmHideColumnTitle,
            modalMessage: localization.confirmHideColumnMessage,
            modalConfirmMessage: localization.hideColumnConfirm,
            onConfirm: handleConfirmHide,
            onCancel: handleCancelHide,
            haveActions: true,
            haveDontAskAgain: true,
            haveFilter: true,
            confirmState: header.id,
          })
        );
        dispatch(openModal());
      }
    };
    /**
    * Handles the key press event, triggering an action if Enter key is pressed.
    *
    * @param {KeyboardEvent} event - The key press event.
    */
      const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
          handleHideIconClick();
        }
      };

      const widthClass = () => {
        if (
          [
            "s6",
            "5057",
            "5343",
            "4680",
            "4369",
            "142",
            "5128",
            "4388",
            "270",
            "3899",
            "4010",
            "6842",
            "4537",
            "4815",
          ].includes(header.id)
        ) {
          return "width-350";
        } else if (["5576", "6752"].includes(header.id)) {
          return "width-500";
        } else {
          return "";
        }
      };

    return header.id ? (
      <th
        active={header.active ? header.active.toString() : 'false'}
        key={header.id}
        data-parameters={header.id}
        className={`header-columns ${
          (newSort === header.id && sortPriority.length>0) ? "active" : ""
        } ${widthClass()}`}
      >
        <DragComponent
          id={header.id}
          draggable={true}
          className={`${header.format} filter${(newSort === header.id && sortPriority.length > 0) ? " active" : ""}`}
          tooltip={header.tooltip}
        >
          <div className="header-columns__container">
            <div className="header-columns__container__header-tools">
              <span className="header-tools__drag-and-drop-span parts__tooltip-wrapper">
                <ToolTip label={dragAndDropLabel} side="right" />
                <img
                  className="header-tools__drag-and-drop"
                  src={dragAndDropIcon}
                  alt="drag and drop"
                ></img>
              </span>
              
              <span onKeyDown={handleKeyDown} className="parts__tooltip-wrapper">
              <ToolTip label={hideColumnLabel} side="left" />
              <img
                src={hideIcon}
                className="header-tools__hideicon"
                tabIndex={0}
                aria-label={`${header.name} ${localization.hideColumn}`}
                alt="hide"
                role="button"
                onClick={handleHideIconClick}
                width="16px"
                height="16px"
              />
              <p className="sr-only">{localization.opensModal} </p>
              </span>
              
            </div>
            <div className="header-columns__container__header-title">
              {header.name}
              {header.delay &&  <span className="delay_label">{header.delay}</span>}
              {header.count && (
                <div className="header-title__header-count"></div>
              )}
              <ColumnSort
                name={header.name}
                parameter={header.id}
                delay={header.delay}
                uom={header.uom}
                setSortStatus={props.setSortStatus}
              />
            </div>
          </div>
        </DragComponent>
      </th>
    ) : null;
  };

  /**
   * @name handleFocusPosition
   * @function
   * @const
   * @description Function to handle the focus after column gets hidden
   */
   const handleFocusPosition = (confirmState) => {
    //When the column is hidden, the focus should shift to the next interactive element - it's the hide icon from the next column
    let columns = document.querySelectorAll(".header-columns");
    for (let i = 0; i < columns.length; i++) {
      //Getting all columns and checking the one that has been clicked using the unique attribute "data-parameters"
      let attributeValue = columns[i].getAttribute("data-parameters");
      if (attributeValue !== null && attributeValue === confirmState) {
        //If there are more columns on the right -> meaning [i+1] exists -> focus on that one
        if(columns[i+1]){
          columns[i+1].getElementsByClassName("header-tools__hideicon")[0].focus();
        }
        //If we are on the last column -> shift the focus to the search box
        else{
          document.querySelector(".search-box").focus();
        }
      }
    }
  };

  /**
   * @name handleConfirmHide
   * @function
   * @const
   * @description Function to handle Confirm in alert modal
   */
  const handleConfirmHide = (dontAskAgainChecked, confirmState) => {
    if (dontAskAgainChecked) {
      localStorage.setItem("dontAskAgainPreference", true);
      setDontAskAgain(true);
    }
    handleFocusPosition(confirmState)
    dispatch(hideParameter(confirmState));
    dispatch(closeModal());
  };

  /**
   * @name handleCancelHide
   * @function
   * @const
   * @description function to close the AlertModal
   */
  const handleCancelHide = () => dispatch(closeModal());

  return <>{props.value.map((header) => renderColumn(header))}</>;
};

Columns.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      count: PropTypes.number,
      delay: PropTypes.string,
      uom: PropTypes.string,
    })
  ),
  sortStatus: PropTypes.string,
  setSortStatus: PropTypes.func,
};

export default Columns;
